
import { lazy } from "react";

export const seoRoutes = [
  {
    path: "/keyword-research",
    component: lazy(() => import("@/pages/KeywordResearchPage")),
    meta: {
      title: "Keyword Research",
      description: "Filter and manage your saved keyword research repository"
    }
  },
  {
    path: "/keyword-hopping",
    component: lazy(() => import("@/pages/KeywordHoppingPage")),
    meta: {
      title: "Keyword Hopping",
      description: "Research new keywords to expand your SEO strategy"
    }
  },
  {
    path: "/keyword-assistant", 
    component: lazy(() => import("@/pages/KeywordAssistantPage")),
    meta: {
      title: "Keyword Assistant",
      description: "AI-powered keyword analysis and suggestions"
    }
  },
  {
    path: "/serps-research",
    component: lazy(() => import("@/pages/SerpsResearchPage")),
    meta: {
      title: "SERPS Research", 
      description: "Search engine results page analysis"
    }
  },
  {
    path: "/ai-overview-research",
    component: lazy(() => import("@/pages/AiOverviewResearchPage")),
    meta: {
      title: "AI Overview Research",
      description: "Google AI Overview research using DataForSEO"
    }
  },
  {
    path: "/keywords-5ws",
    component: lazy(() => import("@/pages/Keywords5wsPage")),
    meta: {
      title: "Keywords 5Ws",
      description: "Comprehensive keyword analysis using 5W framework"
    }
  },
  {
    path: "/my-runs",
    component: lazy(() => import("@/pages/MyRunsPage")),
    meta: {
      title: "My runs",
      description: "Track batch analysis runs (5Ws, etc.) and view job progress"
    }
  },
  {
    path: "/seo-dashboard",
    component: lazy(() => import("@/pages/SeoDashboardPage")),
    meta: {
      title: "SEO Dashboard",
      description: "Overview of SEO performance and metrics"
    }
  },
  {
    path: "/seo-analytics",
    component: lazy(() => import("@/pages/SeoAnalyticsPage")),
    meta: {
      title: "SEO Analytics",
      description: "Detailed SEO analytics and reporting"
    }
  },
  {
    path: "/seo-report",
    component: lazy(() => import("@/pages/SeoReportPage")),
    meta: {
      title: "SEO Report",
      description: "Google Search Console organic search performance report"
    }
  },
  {
    path: "/seo-diagnostic",
    component: lazy(() => import("@/pages/SeoDiagnosticPage")),
    meta: {
      title: "SEO Diagnostic",
      description: "Professional SEO/CRO diagnostic dashboard for detecting metric drops"
    }
  },
  {
    path: "/seo-trends-analysis",
    component: lazy(() => import("@/pages/SeoTrendsAnalysisPage")),
    meta: {
      title: "SEO & Google Trends",
      description: "Identify declining content and discover trend-based recovery opportunities with AI content generation"
    }
  },
  {
    path: "/predictive-analytics",
    component: lazy(() => import("@/pages/PredictiveAnalyticsPage")),
    meta: {
      title: "Predictive Analytics",
      description: "AI-powered SEO predictions and forecasts"
    }
  },
  {
    path: "/3danalysis_step1",
    component: lazy(() => import("@/pages/ThreeDAnalysisPage")),
    meta: {
      title: "3D Analysis Step 1",
      description: "Three-dimensional SEO analysis - Step 1"
    }
  },
  {
    path: "/3danalysis_step2",
    component: lazy(() => import("@/pages/ThreeDAnalysisStep2Page")),
    meta: {
      title: "3D Analysis Step 2", 
      description: "Three-dimensional SEO analysis - Step 2"
    }
  },
  {
    path: "/3danalysis_step3",
    component: lazy(() => import("@/pages/ThreeDAnalysisStep3Page")),
    meta: {
      title: "3D Analysis Step 3",
      description: "Three-dimensional SEO analysis - Step 3"
    }
  },
  {
    path: "/3danalysis_step4",
    component: lazy(() => import("@/pages/ThreeDAnalysisStep4Page")),
    meta: {
      title: "3D Analysis Step 4",
      description: "Three-dimensional SEO analysis - Step 4"
    }
  },
  {
    path: "/onpage-seo-audit",
    component: lazy(() => import("@/pages/OnPageSeoAuditPage")),
    meta: {
      title: "On-Page SEO Audit",
      description: "Comprehensive on-page SEO analysis"
    }
  },
  {
    path: "/getting-top-articles",
    component: lazy(() => import("@/pages/GettingTopArticlesPage")),
    meta: {
      title: "Getting Top Articles",
      description: "Find and analyze top-performing articles"
    }
  },
  {
    path: "/getting-proper-structure",
    component: lazy(() => import("@/pages/GettingProperStructurePage")),
    meta: {
      title: "Getting Proper Structure",
      description: "Optimize content structure for SEO"
    }
  },
  {
    path: "/seo-selection-groups",
    component: lazy(() => import("@/pages/SeoSelectionGroupsPage")),
    meta: {
      title: "SEO Selection Groups",
      description: "Manage SEO keyword selection groups"
    }
  },
  {
    path: "/historical-declines",
    component: lazy(() => import("@/pages/HistoricalDeclinesPage")),
    meta: {
      title: "Historical Declines",
      description: "Analyze historical SEO performance declines"
    }
  },
  {
    path: "/branded-keywords",
    component: lazy(() => import("@/pages/BrandedKeywordsPage")),
    meta: {
      title: "Branded Keywords Classification",
      description: "Classify and manage branded keywords"
    }
  },
  {
    path: "/seo/internal-linking",
    component: lazy(() => import("@/pages/seo/InternalLinkingAnalysis")),
    meta: {
      title: "Internal Linking Analysis",
      description: "Discover high-priority internal linking opportunities using TF-IDF tokenization"
    }
  },
  {
    path: "/seo/internal-linking/crawled-pages",
    component: lazy(() => import("@/pages/seo/InternalLinkingCrawledPages")),
    meta: {
      title: "Crawled Pages",
      description: "View all crawled pages for internal linking analysis"
    }
  },
  {
    path: "/seo/internal-linking/tokens",
    component: lazy(() => import("@/pages/seo/InternalLinkingTokens")),
    meta: {
      title: "Extracted Tokens",
      description: "View keywords extracted using TF-IDF analysis"
    }
  },
  {
    path: "/seo/internal-linking/enriched-keywords",
    component: lazy(() => import("@/pages/seo/InternalLinkingEnrichedKeywords")),
    meta: {
      title: "Enriched Keywords",
      description: "View keywords enriched with search metrics"
    }
  },
  {
    path: "/seo/internal-linking/opportunities",
    component: lazy(() => import("@/pages/seo/InternalLinkingOpportunities")),
    meta: {
      title: "Internal Linking Opportunities",
      description: "View and manage internal linking opportunities"
    }
  },
  {
    path: "/competitors-keywords",
    component: lazy(() => import("@/pages/CompetitorsKeywordsPage")),
    meta: {
      title: "Competitors Keywords",
      description: "Analyze competitor keyword strategies"
    }
  },
  {
    path: "/competitors-content",
    component: lazy(() => import("@/pages/CompetitorsContentPage")),
    meta: {
      title: "Competitors Content",
      description: "Analyze competitor content pages - blogs, articles, and guides"
    }
  },
  {
    path: "/competitors-backlinks",
    component: lazy(() => import("@/pages/CompetitorsBacklinksPage")),
    meta: {
      title: "Competitors Backlinks",
      description: "Analyze competitor backlink profiles and strategies"
    }
  },
  {
    path: "/content-briefs",
    component: lazy(() => import("@/pages/ContentBriefsPage")),
    meta: {
      title: "Content Briefs",
      description: "Create and manage content briefs"
    }
  },
  {
    path: "/content-ideas",
    component: lazy(() => import("@/pages/ContentIdeasPage")),
    meta: {
      title: "Content Ideas",
      description: "Generate content ideas using AI and vector storage"
    }
  },
  {
    path: "/enhanced-content-ideas",
    component: lazy(() => import("@/pages/EnhancedContentIdeasPage")),
    meta: {
      title: "Enhanced Content Ideas",
      description: "Generate data-driven content ideas with competitor analysis, keyword research, and persona insights"
    }
  },
  {
    path: "/ideation-prompts",
    component: lazy(() => import("@/pages/settings/IdeationPromptsPage")),
    meta: {
      title: "Ideation Prompts",
      description: "Manage AI prompts for content idea generation"
    }
  },
  {
    path: "/cm-settings",
    component: lazy(() => import("@/pages/ContentMarketingSettingsPage")),
    meta: {
      title: "Content Marketing Settings",
      description: "Configure content marketing tools and settings"
    }
  },
  {
    path: "/seo-testing",
    component: lazy(() => import("@/pages/SeoTestingPage")),
    meta: {
      title: "SEO Testing Suite",
      description: "Time-based and split-URL SEO testing with statistical analysis"
    }
  },
  {
    path: "/seo-test-create",
    component: lazy(() => import("@/pages/SeoTestCreatePage")),
    meta: {
      title: "Create SEO Test",
      description: "Create a new SEO test with keyword classifications data"
    }
  },
  {
    path: "/seo-testing/data/:testId",
    component: lazy(() => import("@/pages/SeoTestDataPage")),
    meta: {
      title: "SEO Test Data",
      description: "View detailed SEO test data and performance metrics"
    }
  },
  {
    path: "/google-impact",
    component: lazy(() => import("@/pages/GoogleImpactPage")),
    meta: {
      title: "Google Impact Analysis",
      description: "Comprehensive Google impact insights for your SEO performance"
    }
  },
  {
    path: "/causality-report",
    component: lazy(() => import("@/pages/CausalityReportPage")),
    meta: {
      title: "Causality Report",
      description: "Comprehensive causality analysis for your SEO performance"
    }
  },
  {
    path: "/google-annotations",
    component: lazy(() => import("@/pages/GoogleImpactPage")),
    meta: {
      title: "Google Annotations",
      description: "View and analyze Google algorithm updates and their impact"
    }
  },
  {
    path: "/business-annotations",
    component: lazy(() => import("@/pages/BusinessAnnotationsPage")),
    meta: {
      title: "Business Annotations",
      description: "Track and manage business events that impact SEO performance"
    }
  },
  {
    path: "/local-seo",
    component: lazy(() => import("@/pages/LocalSeoPage")),
    meta: {
      title: "Local SEO",
      description: "Manage Google My Business integration and local SEO performance"
    }
  },
  {
    path: "/appstore-seo",
    component: lazy(() => import("@/pages/AppStoreSEOPage")),
    meta: {
      title: "AppStore SEO (ASO) - Mobile App Optimization",
      description: "Optimize your mobile apps for better app store visibility, rankings, and downloads with AI-powered ASO suggestions."
    }
  },
  {
    path: "/appstore-seo-keywords",
    component: lazy(() => import("@/pages/AppStoreSEOKeywordsPage")),
    meta: {
      title: "ASO Keywords - Track App Store Rankings",
      description: "Track and monitor your mobile app's keyword rankings across Google Play Store and Apple App Store."
    }
  },
  {
    path: "/appstore-seo-suggestions",
    component: lazy(() => import("@/pages/AppStoreSEOSuggestionsPage")),
    meta: {
      title: "ASO Suggestions - AI App Optimization",
      description: "Get AI-powered optimization suggestions for your mobile app's title, description, and keywords."
    }
  },
  {
    path: "/quizzes",
    component: lazy(() => import("@/pages/QuizListPage")),
    meta: {
      title: "Interactive Quizzes",
      description: "Create and manage interactive quizzes for lead generation and engagement"
    }
  },
  {
    path: "/quizzes/builder",
    component: lazy(() => import("@/pages/QuizBuilderPage")),
    meta: {
      title: "Create Quiz",
      description: "Build a new interactive quiz"
    }
  },
  {
    path: "/quizzes/builder/:quizId",
    component: lazy(() => import("@/pages/QuizBuilderPage")),
    meta: {
      title: "Edit Quiz",
      description: "Edit an existing quiz"
    }
  },
  {
    path: "/quiz-styles",
    component: lazy(() => import("@/pages/QuizStylesPage")),
    meta: {
      title: "Quiz Styles",
      description: "Manage visual styles for your quizzes"
    }
  },
  {
    path: "/faq-generator",
    component: lazy(() => import("@/pages/FaqGenerator")),
    meta: {
      title: "FAQ Generator",
      description: "Generate SEO-optimized FAQ content from People Also Ask questions"
    }
  }
];
